import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { Printer, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal that fetches the kiosk-deep-link QR payload for a single unit and
 * renders it big, with Print + Copy actions. The QR encodes the kiosk URL
 * returned by GET /api/public/unit-qr/{unit_id}/info, so scanning it on any
 * phone just opens the Visitor Kiosk pre-filled for that unit.
 */
export function UnitQRModal({ open, onOpenChange, unitId }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const printAreaRef = useRef(null);

  useEffect(() => {
    if (!open || !unitId) return;
    let active = true;
    setLoading(true);
    setInfo(null);
    setCopied(false);
    api
      .get(`/public/unit-qr/${unitId}/info`, { withCredentials: false })
      .then(({ data }) => { if (active) setInfo(data); })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Failed to load unit QR");
        onOpenChange?.(false);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, unitId, onOpenChange]);

  async function copyLink() {
    if (!info?.kiosk_url) return;
    try {
      await navigator.clipboard.writeText(info.kiosk_url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  }

  function printQr() {
    if (!printAreaRef.current) return;
    const html = printAreaRef.current.innerHTML;
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) {
      toast.error("Pop-up blocked — allow pop-ups to print");
      return;
    }
    w.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8"/>
    <title>FastVi · Unit ${info?.unit_number ?? ""} QR</title>
    <style>
      @page { margin: 24mm; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
        margin: 0; padding: 32px;
        color: #111;
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        min-height: 90vh;
      }
      .brand { font-size: 32px; font-weight: 900; letter-spacing: -0.02em; margin-bottom: 8px; }
      .brand span { color: #FF6B00; }
      .label { font-size: 11px; letter-spacing: 0.3em; text-transform: uppercase; color: #666; margin-bottom: 24px; }
      .card { border: 2px solid #111; border-radius: 24px; padding: 36px 48px; text-align: center; }
      .headline { font-size: 36px; font-weight: 800; margin: 0 0 4px; letter-spacing: -0.02em; }
      .sub { font-size: 15px; color: #333; margin-bottom: 28px; }
      canvas, svg { width: 320px !important; height: 320px !important; }
      .url { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; color: #555; margin-top: 24px; word-break: break-all; max-width: 420px; }
      .foot { margin-top: 32px; font-size: 11px; color: #888; letter-spacing: 0.15em; text-transform: uppercase; }
    </style>
  </head>
  <body>
    <div class="brand">Fast<span>Vi</span></div>
    <div class="label">Scan to ring this unit</div>
    <div class="card">
      <h1 class="headline">Unit ${info?.unit_number ?? ""}</h1>
      <div class="sub">${info?.building_name ?? ""} · ${info?.building_code ?? ""}</div>
      ${html}
      <div class="url">${info?.kiosk_url ?? ""}</div>
    </div>
    <div class="foot">Place near the lobby entrance · FastVi secure intercom</div>
    <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 400); };</script>
  </body>
</html>`);
    w.document.close();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="unit-qr-modal">
        <DialogHeader>
          <DialogTitle>
            {loading || !info
              ? "Loading unit QR…"
              : `Scan to ring Unit ${info.unit_number} · ${info.building_name}`}
          </DialogTitle>
          <DialogDescription>
            Visitors scan this with any phone camera and the FastVi Kiosk opens
            pre-filled for this unit. No app install needed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center py-4">
          {loading || !info ? (
            <div className="h-64 w-64 grid place-items-center text-muted-foreground"
                 data-testid="unit-qr-loading">
              <Loader2 className="animate-spin" size={28}/>
            </div>
          ) : (
            <>
              <div
                ref={printAreaRef}
                className="rounded-xl bg-white p-5 border border-border"
                data-testid="unit-qr-canvas"
              >
                <QRCodeCanvas
                  value={info.kiosk_url}
                  size={240}
                  level="M"
                  includeMargin
                />
              </div>
              <div className="mt-4 text-xs font-mono text-muted-foreground break-all text-center max-w-xs"
                   data-testid="unit-qr-url">
                {info.kiosk_url}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            onClick={copyLink}
            disabled={!info}
            data-testid="unit-qr-copy"
          >
            {copied ? <Check size={14} className="mr-1.5"/> : <Copy size={14} className="mr-1.5"/>}
            {copied ? "Copied" : "Copy link"}
          </Button>
          <Button
            onClick={printQr}
            disabled={!info}
            data-testid="unit-qr-print"
          >
            <Printer size={14} className="mr-1.5"/> Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
